import * as React from 'react';
import { useFormContext, type FieldPath, type FieldValues } from 'react-hook-form';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RequiredMark,
} from './Form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * What Radix hands back when it has no value. It refuses a SelectItem whose
 * value is the empty string, so this can never be a real user pick.
 */
const NO_VALUE = '';

export interface FormSelectOption {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
}

interface FormSelectProps<TValues extends FieldValues> {
  name: FieldPath<TValues>;
  label?: React.ReactNode;
  description?: React.ReactNode;
  placeholder?: string;
  options: FormSelectOption[];
  disabled?: boolean;
  /** Marks the label with `*` and sets `aria-required` on the trigger; the schema still enforces it. */
  required?: boolean;
}

export function FormSelect<TValues extends FieldValues>({
  name,
  label,
  description,
  placeholder,
  options,
  disabled,
  required,
}: FormSelectProps<TValues>) {
  const { control } = useFormContext<TValues>();
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {label !== undefined && (
            <FormLabel>
              {label}
              {required && <RequiredMark />}
            </FormLabel>
          )}
          <Select
            // The hidden native select Radix mounts inside a form is what emits
            // NO_VALUE: when the controlled value changes (a `form.reset` once
            // the row loads) before that select's options have caught up,
            // assigning the new value leaves it on the sentinel and its change
            // event round-trips that back here — silently blanking a field the
            // operator had set. Dropping the sentinel keeps the loaded value.
            onValueChange={(next) => {
              if (next !== NO_VALUE) field.onChange(next);
            }}
            value={(field.value as string | undefined) ?? NO_VALUE}
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger aria-required={required || undefined}>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
